import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

function formatEok(value, digits = 3) {
  if (value === null || value === undefined) return '인피니티';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: digits })}억`;
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function deckCode(item) {
  return String(item?.code ?? item?.nikke_id ?? item?.id ?? '').trim();
}

function DeckCharacter({ item, index }) {
  const code = deckCode(item) || `S${index + 1}`;
  const imageUrl = String(item?.imageUrl || item?.image_url || '').trim();
  const name = String(item?.name || code).trim();
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <span className="raid-detail-deck-character" title={name}>
      {imageUrl && !imageFailed
        ? <img src={imageUrl} alt={name} loading="lazy" onError={() => setImageFailed(true)} />
        : <b>{code}</b>}
    </span>
  );
}

function DeckCodes({ deck }) {
  const items = Array.isArray(deck) ? deck : [];
  if (!items.length) return <span className="raid-detail-deck-empty">덱 정보 없음</span>;
  return (
    <div className="raid-detail-deck-codes" aria-label="대표 덱">
      {items.map((item, index) => {
        const code = deckCode(item) || `S${index + 1}`;
        return <DeckCharacter item={item} index={index} key={`${code}-${index}`} />;
      })}
    </div>
  );
}

export function RaidBossDetailModal({ boss, records = [], onClose }) {
  const [tab, setTab] = useState('sum');
  const [selectedPhase, setSelectedPhase] = useState(() => boss?.currentPhase || 1);

  useEffect(() => {
    setSelectedPhase(boss?.currentPhase || 1);
    setTab('sum');
  }, [boss?.bossNo, boss?.currentPhase]);

  useEffect(() => {
    const handler = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const phases = useMemo(() => (boss?.phases || []).filter((phase) => phase.phaseNo <= 3 || (boss?.bossNo === 5 && phase.phaseNo === 4)), [boss]);
  const selected = phases.find((phase) => phase.phaseNo === selectedPhase) || phases[0];
  const visibleRecords = useMemo(
    () => (Array.isArray(records) ? records : []).filter((record) => record.bossNo === boss?.bossNo && record.phaseNo === selectedPhase),
    [boss?.bossNo, records, selectedPhase],
  );
  const memberRows = useMemo(() => {
    const map = new Map();
    for (const record of visibleRecords) {
      const key = record.memberOpenid || record.nickname;
      const current = map.get(key) || {
        key,
        nickname: record.nickname || '닉네임 확인 불가',
        attackCount: 0,
        damageEok: 0,
        representative: record,
      };
      current.attackCount += 1;
      current.damageEok += Number(record.damageEok || 0);
      if (Number(record.damageEok || 0) > Number(current.representative?.damageEok || 0)) current.representative = record;
      map.set(key, current);
    }
    return [...map.values()].sort((a, b) => b.damageEok - a.damageEok || a.nickname.localeCompare(b.nickname, 'ko'));
  }, [visibleRecords]);

  const averageDamage = Number(boss?.averageDamageEok || 0);

  if (!boss || !selected) return null;
  const bossName = bossDisplayName(boss);

  return (
    <div className="raid-detail-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose?.();
    }}>
      <section className="raid-detail-modal" role="dialog" aria-modal="true" aria-label={`${bossName} 실전 딜 상세`}>
        <header className="raid-detail-head">
          <div>
            <span>{bossName} · {boss.element || '속성 미정'}</span>
            <h2>{bossName} 실전 딜 상세</h2>
          </div>
          <button type="button" onClick={onClose}>닫기</button>
        </header>

        <div className="raid-detail-hero">
          <div className="raid-detail-image" style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }}>
            {boss.isCleared ? <strong>CLEAR</strong> : null}
          </div>
          <div className="raid-detail-main">
            <div className="raid-detail-phase-tabs">
              {phases.map((phase) => (
                <button
                  className={`${selectedPhase === phase.phaseNo ? 'active' : ''} ${phase.cleared ? 'done' : ''}`}
                  key={phase.phaseNo}
                  type="button"
                  onClick={() => setSelectedPhase(phase.phaseNo)}
                >
                  {phase.phaseNo}단{phase.isInfinite ? ' · 인피니티' : phase.cleared ? ' · 처치' : ''}
                </button>
              ))}
            </div>
            <h3>{selected.phaseNo}단 기록</h3>
            <div className={`raid-detail-hp-track ${selected.isInfinite ? 'infinite' : ''}`}>
              <span style={{ width: `${selected.isInfinite ? 100 : selected.remainingPercent}%` }} />
            </div>
            <p>{selected.isInfinite ? `누적 실전 딜 ${formatEok(selected.damageEok)}` : `남은 HP ${formatRaidHpEok(selected.remainingHpEok)} / ${formatRaidHpEok(selected.maxHpEok)}`}</p>
          </div>
        </div>

        <div className="raid-detail-phase-grid">
          {phases.map((phase) => (
            <button
              className={`${selectedPhase === phase.phaseNo ? 'active' : ''} ${phase.cleared ? 'done' : ''}`}
              key={phase.phaseNo}
              type="button"
              onClick={() => setSelectedPhase(phase.phaseNo)}
            >
              <span>{phase.phaseNo}단</span>
              <strong>{phase.isInfinite ? '인피니티' : formatRaidHpEok(phase.remainingHpEok)}</strong>
              <small>{phase.attackCount}타 · 딜 {formatEok(phase.damageEok)}</small>
              <div><i style={{ width: `${phase.isInfinite ? 100 : phase.remainingPercent}%` }} /></div>
            </button>
          ))}
        </div>

        <div className="raid-detail-kpis">
          <article><span>선택 단계 실전 딜</span><strong>{formatEok(selected.damageEok)}</strong></article>
          <article><span>공격 수</span><strong>{selected.attackCount}타</strong></article>
          <article><span>전체 단수 평균 딜</span><strong>{formatEok(averageDamage)}</strong></article>
          <article><span>남은 비율</span><strong>{selected.isInfinite ? '∞' : formatPercent(selected.remainingPercent)}</strong></article>
        </div>

        <div className="raid-detail-table-tabs">
          <button className={tab === 'sum' ? 'active' : ''} type="button" onClick={() => setTab('sum')}>멤버별 합산</button>
          <button className={tab === 'log' ? 'active' : ''} type="button" onClick={() => setTab('log')}>공격 로그</button>
        </div>

        <div className="raid-detail-table-wrap">
          {tab === 'sum' ? (
            <table>
              <thead><tr><th>멤버</th><th>공격</th><th>합산 딜</th><th>대표 덱</th></tr></thead>
              <tbody>
                {memberRows.map((row) => (
                  <tr key={row.key}><td>{row.nickname}</td><td>{row.attackCount}회</td><td>{formatEok(row.damageEok)}</td><td><DeckCodes deck={row.representative?.deck} /></td></tr>
                ))}
                {!memberRows.length ? <tr><td colSpan="4">선택한 단계의 공격 기록이 없습니다.</td></tr> : null}
              </tbody>
            </table>
          ) : (
            <table>
              <thead><tr><th>멤버</th><th>타수</th><th>딜량</th><th>막타</th><th>덱</th></tr></thead>
              <tbody>
                {visibleRecords.map((record) => (
                  <tr key={record.recordKey}><td>{record.nickname}</td><td>{record.attackNo ? `${record.attackNo}타` : '-'}</td><td>{formatEok(record.damageEok)}</td><td>{record.isKill ? 'O' : ''}</td><td><DeckCodes deck={record.deck} /></td></tr>
                ))}
                {!visibleRecords.length ? <tr><td colSpan="5">선택한 단계의 공격 기록이 없습니다.</td></tr> : null}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
